import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    useLoaderData,
    useNavigate,
    useSearchParams,
    useSubmit,
} from "react-router";
import {
    Image,
    Navbar,
    Card,
    Form,
    Stack,
    Row,
    Col,
    InputGroup,
    Button,
    Badge,
} from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTrash,
    faLink,
    faXmark,
    faBell,
    faCircleInfo,
    faEllipsisV,
    faPlus,
    faArrowUp,
    faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { device } from "constants/Core";
import { useTheme } from "components/shared/ThemeProvider";
import ConfirmCancelDialog from "components/shared/ConfirmDialog";
import {
    renderSavingProgressToast,
    renderSimpleToast,
    renderLoadingProgressToast,
} from "components/shared/Toasts";
import imagePlaceHolder from "assets/images/product_image_placeholder.png";
import { trackImpressions, trackClick } from "services/feedbackService";

export async function loader() {
    const records = await renderLoadingProgressToast(
        axios.get("wishlistitem/list")
    ).then((response) => response.data);
    return { records };
}

export async function action({ request }) {
    const updates = await request.json();
    await renderSavingProgressToast(
        axios.delete(`wishlistitem/${updates["id"]}`)
    );
    return null;
}

const FloatingMenuContainer = styled.div`
    position: fixed;
    bottom: 32px;
    right: 32px;
    z-index: 1100;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    @media (orientation: landscape) {
        right: 64px;
    }
`;

const FabButton = styled.button`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #007bff;
    color: #fff;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    margin-top: 12px;
    cursor: pointer;
    transition: background 0.2s, transform 0.3s, opacity 0.3s;
    opacity: ${(props) => (props.visible ? 1 : 0)};
    transform: ${(props) =>
        props.visible ? "translateY(0)" : "translateY(20px)"};
    pointer-events: ${(props) => (props.visible ? "auto" : "none")};
    &:hover {
        background: #0056b3;
    }
`;

const BackToTopButton = styled(FabButton)`
    background: #6c757d;
    &:hover {
        background: #495057;
    }
`;

const MenuFabButton = styled(FabButton)`
    background: #343a40;
    &:hover {
        background: #23272b;
    }
    opacity: 1;
    transform: none;
    pointer-events: auto;
`;

const ProductOriginText = styled.div`
    color: light-grey;
    font-size: 0.8em;
    opacity: 0.7;
`;

const ProductImage = styled(Image)`
    @media ${device.mobileS} {
        width: 5em;
        min-width: 5em;
        min-height: 5em;
    }

    @media ${device.laptop} {
        width: 9.6em;
        min-width: 9.6em;
        min-height: 9.6em;
    }

    text-color: ${(props) => (props.$isDarkMode ? "#000000" : "#ffffff")};
`;

const ProductDetail = styled(Card)`
    width: 100%;
    min-width: 0;
    border-style: none;
    background-color: transparent !important;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#000000")};
`;

const ProductDetailBody = styled(Card.Body)`
    background-color: transparent !important;
    padding: 0 !important;
`;

const ProductExternalLink = styled.a`
    color: ${(props) => (props.$isDarkMode ? "white" : "#000")} !important;
    text-decoration: none;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const WishlistItem = ({ record, onDelete }) => {
    const nav = useNavigate();
    const { isDarkMode } = useTheme();

    const copyLink = () => {
        navigator.clipboard.writeText(record.product_url);
        renderSimpleToast("Copied link to clipboard");
    };
    return (
        <Col className="mb-3" md={12} lg={9}>
            <div className="d-flex align-items-start gap-4">
                <ProductDetail $isDarkMode={isDarkMode}>
                    <ProductDetailBody>
                        <ProductDetail.Text
                            style={{
                                minWidth: 0,
                                overflow: "hidden",
                            }}
                        >
                            <ProductExternalLink
                                href={record.product_url}
                                target="_blank"
                                $isDarkMode={isDarkMode}
                            >
                                {record.product_name}
                            </ProductExternalLink>
                        </ProductDetail.Text>

                        <ProductOriginText
                            as="div"
                            data-testid="txt_product_origin"
                        >
                            {record.origin}
                        </ProductOriginText>

                        <Stack
                            direction="horizontal"
                            gap={2}
                            className="mt-1 align-items-center"
                        >
                            <Button
                                variant="link"
                                className="p-0"
                                title="View"
                                onClick={() => nav(`/wishlist/${record.id}`)}
                            >
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </Button>
                            <Button
                                variant="link"
                                className="p-0 text-danger"
                                title="Delete"
                                onClick={() => onDelete(record)}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                            <Button
                                variant="link"
                                className="p-0"
                                title="Copy Link"
                                onClick={copyLink}
                            >
                                <FontAwesomeIcon icon={faLink} />
                            </Button>
                            {(record.item_events_preview?.length > 0 ||
                                record.tracking_event_count > 0) && (
                                <>
                                    <span className="text-muted">|</span>
                                    {record.item_events_preview?.length > 0 && (
                                        <span
                                            className="text-primary"
                                            style={{ cursor: "pointer" }}
                                            title="Has events"
                                            onClick={() =>
                                                nav(
                                                    `/wishlist/${record.id}#events`
                                                )
                                            }
                                        >
                                            <FontAwesomeIcon icon={faBell} />
                                        </span>
                                    )}
                                    {record.tracking_event_count > 0 && (
                                        <span
                                            className="text-danger"
                                            style={{ cursor: "pointer" }}
                                            title="Has warnings"
                                            onClick={() =>
                                                nav(
                                                    `/wishlist/${record.id}#events`
                                                )
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faExclamationTriangle}
                                            />
                                        </span>
                                    )}
                                </>
                            )}
                        </Stack>
                        {record.tags?.length > 0 && (
                            <Stack
                                direction="horizontal"
                                gap={1}
                                className="mt-1 flex-wrap"
                            >
                                {record.tags.map((tag) => (
                                    <Badge
                                        key={tag.name}
                                        bg="secondary"
                                        style={{ fontWeight: "normal" }}
                                    >
                                        {tag.name}
                                    </Badge>
                                ))}
                            </Stack>
                        )}
                        {record.item_events_preview?.length > 0 && (
                            <Stack gap={1} className="mt-1">
                                {record.item_events_preview.map(
                                    (preview, idx) => (
                                        <div
                                            key={idx}
                                            className="text-muted"
                                            style={{
                                                fontSize: "0.85em",
                                                cursor: "pointer",
                                                borderLeft: "2px solid #dee2e6",
                                                paddingLeft: "0.5em",
                                            }}
                                            title={preview.description}
                                            onClick={() => {
                                                const phrases =
                                                    preview.data?.matches
                                                        ?.map(
                                                            (m) =>
                                                                m.matched_text
                                                        )
                                                        .filter(Boolean);
                                                const fragment =
                                                    preview.data?.text;
                                                const url =
                                                    fragment &&
                                                    preview.code ===
                                                        "PROMOTION_DETECTED"
                                                        ? `${
                                                              record.product_url
                                                          }#:~:text=${encodeURIComponent(
                                                              fragment
                                                          )}`
                                                        : record.product_url;
                                                if (phrases?.length > 0)
                                                    trackClick(
                                                        phrases,
                                                        preview.id
                                                    );
                                                window.open(
                                                    url,
                                                    "_blank",
                                                    "noopener,noreferrer"
                                                );
                                            }}
                                        >
                                            {preview.description?.length > 100
                                                ? preview.description.slice(
                                                      0,
                                                      100
                                                  ) + "…"
                                                : preview.description}
                                        </div>
                                    )
                                )}
                            </Stack>
                        )}
                    </ProductDetailBody>
                </ProductDetail>
                <ProductImage
                    data-testid="img_product_image"
                    src={record.image_url ? record.image_url : imagePlaceHolder}
                    alt="Image Loading Error"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = imagePlaceHolder;
                    }}
                    rounded
                />
            </div>
        </Col>
    );
};

const WishlistListView = () => {
    const { records } = useLoaderData();
    const [searchParams] = useSearchParams();
    const [query, setQuery] = useState();
    const [selection, setSelection] = useState();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [filterSetting, setFilterSetting] = useState(() => {
        const init = {};
        if (searchParams.get("with_item_event")) init.with_item_event = "on";
        if (searchParams.get("with_tracking_event"))
            init.with_tracking_event = "on";
        return init;
    });
    const [fabOpen, setFabOpen] = useState(false);
    const submit = useSubmit();
    const [showBackToTop, setShowBackToTop] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        const events = (records || [])
            .flatMap((r) => r.item_events_preview || [])
            .map((p) => ({
                event_id: p.id,
                phrases: (p.data?.matches || [])
                    .map((m) => m.matched_text)
                    .filter(Boolean),
            }))
            .filter((e) => e.phrases.length > 0);
        if (events.length > 0) {
            trackImpressions(events);
        }
    }, [records]);

    const getQuery = () => {
        return (
            records
                // filter by search text
                .filter(
                    (item) =>
                        !query ||
                        // product name
                        item.product_name.match(new RegExp(query, "i")) ||
                        // origin
                        item.origin.match(new RegExp(query, "i")) ||
                        // product event preview texts
                        item.item_events_preview?.some((preview) =>
                            preview.description?.match(new RegExp(query, "i"))
                        ) ||
                        // tags
                        item.tags?.some((tag) =>
                            tag.name.match(new RegExp(query, "i"))
                        )
                )
                // filter by item event
                .filter(
                    (item) =>
                        filterSetting.with_item_event !== "on" ||
                        item.item_events_preview?.length > 0
                )
                // filter by tracking event
                .filter(
                    (item) =>
                        filterSetting.with_tracking_event !== "on" ||
                        item.tracking_event_count > 0
                )
        );
    };

    const handleConfirmDelete = async (event) => {
        setShowConfirmDialog(false);
        if (event === "confirm") {
            setShowConfirmDialog(false);
            await submit(
                { id: selection.id },
                { method: "delete", encType: "application/json" }
            );
        }
    };

    const onDeleteItem = (record) => {
        setSelection(record);
        setShowConfirmDialog(true);
    };

    const onSearchBoxKeyDown = (e) => {
        if (e.key === "Enter" || e.keyCode === 13) {
            // skip submit form when user press Enter
            e.preventDefault();
            return false;
        }
    };

    const onSearchBoxChange = (e) => {
        setQuery(e.target.value);
    };

    const onClearSearchBox = (e) => {
        document.getElementById("tb_search").value = "";
        setQuery(null);
    };

    const toggleFilter = (key) => {
        const newSetting = { ...filterSetting };
        if (newSetting[key] === "on") {
            delete newSetting[key];
        } else {
            newSetting[key] = "on";
        }
        setFilterSetting(newSetting);
    };

    React.useEffect(() => {
        const newSetting = {};
        if (searchParams.get("with_item_event"))
            newSetting.with_item_event = "on";
        if (searchParams.get("with_tracking_event"))
            newSetting.with_tracking_event = "on";
        setFilterSetting(newSetting);
    }, [searchParams]);

    // Floating menu state
    React.useEffect(() => {
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 200);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleBackToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setFabOpen(false);
    };

    return (
        <>
            <Navbar as={Row}>
                <Col md={12} lg={9} className="d-flex flex-column gap-2">
                    <InputGroup>
                        <Form.Control
                            id="tb_search"
                            onKeyDown={onSearchBoxKeyDown}
                            onChange={onSearchBoxChange}
                            type="text"
                            placeholder="Search"
                            data-testid="input_searchbox"
                            style={{
                                backgroundColor: "white",
                                color: "black",
                                border: "1px solid #ced4da",
                            }}
                        />
                        <Button
                            variant="outline-secondary"
                            onClick={onClearSearchBox}
                            data-testid="btn_clear"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </Button>
                    </InputGroup>
                    <Stack direction="horizontal" gap={2}>
                        <Button
                            variant={
                                filterSetting.with_item_event === "on"
                                    ? "primary"
                                    : "outline-secondary"
                            }
                            size="sm"
                            className="rounded-pill"
                            onClick={() => toggleFilter("with_item_event")}
                            data-testid="chk_switch_item_event"
                        >
                            <FontAwesomeIcon icon={faBell} /> Event
                        </Button>
                        <Button
                            variant={
                                filterSetting.with_tracking_event === "on"
                                    ? "warning"
                                    : "outline-secondary"
                            }
                            size="sm"
                            className="rounded-pill"
                            onClick={() => toggleFilter("with_tracking_event")}
                            data-testid="chk_switch_tracking_event"
                        >
                            <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                            Warning
                        </Button>
                    </Stack>
                </Col>
            </Navbar>
            <div className="d-flex justify-content-start my-2">
                <span>Total Items: {getQuery().length}</span>
            </div>
            <Row className="d-grid gap-3">
                {records.length ? (
                    getQuery().map((item) => (
                        <WishlistItem
                            key={item.id}
                            record={item}
                            onDelete={onDeleteItem}
                        />
                    ))
                ) : (
                    <p>
                        <i>No item</i>
                    </p>
                )}
            </Row>
            <ConfirmCancelDialog
                show={showConfirmDialog}
                callback={handleConfirmDelete}
            >
                Are you sure to delete this item?
            </ConfirmCancelDialog>
            {/* Floating Expandable FAB Menu */}
            <FloatingMenuContainer
                tabIndex={0}
                onBlur={(e) => {
                    // Only close if focus moves outside the menu
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                        setFabOpen(false);
                    }
                }}
            >
                <BackToTopButton
                    aria-label="Back to Top"
                    visible={fabOpen && showBackToTop}
                    onClick={handleBackToTop}
                    style={{ transitionDelay: fabOpen ? "0.1s" : "0s" }}
                >
                    <FontAwesomeIcon icon={faArrowUp} />
                </BackToTopButton>
                <FabButton
                    aria-label="Add"
                    visible={fabOpen}
                    onClick={() => {
                        nav("/wishlist/create");
                        setFabOpen(false);
                    }}
                    style={{
                        background: "#007bff",
                        transitionDelay: fabOpen ? "0.2s" : "0s",
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </FabButton>
                <MenuFabButton
                    aria-label="Menu"
                    onClick={() => setFabOpen((open) => !open)}
                    style={{ marginTop: fabOpen ? "12px" : "0" }}
                >
                    <FontAwesomeIcon icon={faEllipsisV} />
                </MenuFabButton>
            </FloatingMenuContainer>
        </>
    );
};

export default WishlistListView;
